// Register.tsx
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import React, { useState } from 'react';
import { AuthLayout } from '@/components/layouts/AuthLayout';
import { FcGoogle } from 'react-icons/fc'; // Import Google icon
import { Eye, EyeOff } from 'lucide-react'; // Import eye icons for password visibility
import { Label } from '@/components/ui/label';

interface RegistrationData {
  name: string;
  email: string;
  password: string;
  emailOpt: boolean;
}

interface RegisterProps {
  onSubmit: (registrationData: RegistrationData) => Promise<void>;
  onBackToLogin: () => void;
  onGoogleSignIn?: () => Promise<void>; // Add Google sign-in handler
  error?: string;
}

export function Register({
  onSubmit,
  onBackToLogin,
  onGoogleSignIn,
  error: propError,
}: RegisterProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [emailOpt, setEmailOpt] = useState(false);
  const [localError, setLocalError] = useState('');
  const [passwordValid, setPasswordValid] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(true);

  // Password validation function
  const validatePassword = (pass: string) => {
    // At least 5 characters and 1 special character
    const minLength = pass.length >= 5;
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(pass);
    return minLength && hasSpecialChar;
  };

  // Update password validation on change
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setPasswordValid(validatePassword(newPassword));

    // Check if passwords match
    if (confirmPassword) {
      setPasswordsMatch(newPassword === confirmPassword);
    }
  };

  // Update password match validation when confirm password changes
  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newConfirmPassword = e.target.value;
    setConfirmPassword(newConfirmPassword);
    setPasswordsMatch(password === newConfirmPassword);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Validate password before submission
    if (!validatePassword(password)) {
      setLocalError(
        'Password must be at least 5 characters long and contain at least one special character.'
      );
      return;
    }

    // Check if passwords match
    if (password !== confirmPassword) {
      setLocalError('Passwords do not match.');
      return;
    }

    try {
      await onSubmit({ name, email, password, emailOpt });
      setLocalError('');
    } catch (error) {
      setLocalError('An error occurred during registration.');
    }
  };

  const displayError = propError || localError;

  // Add this before the email Input component
  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  return (
    <AuthLayout>
      <div className="p-8 rounded-2xl bg-gradient-to-br from-blue-500/10 via-transparent to-purple-500/10 border border-white/10">
        <h2 className="text-3xl font-bold text-white mb-6 text-center">
          Create Account
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            placeholder="Name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="bg-black border-white/10 text-white placeholder:text-gray-400"
          />
          <div className="mt-4">
            <Label className="text-white/80" htmlFor="email">
              Email
            </Label>
            <Input
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {/* Email validation indicator */}
            {email.length > 0 && (
              <div
                className={`text-xs px-2 py-1.5 border rounded flex items-center gap-2 mt-1 ${isValidEmail(email) ? 'bg-green-500/10 border-green-500/30 text-green-400' : 'bg-red-500/10 border-red-500/30 text-red-400'}`}
              >
                <div
                  className={`w-3 h-3 rounded-full ${isValidEmail(email) ? 'bg-green-500' : 'bg-red-500'}`}
                ></div>
                <p>
                  {isValidEmail(email)
                    ? 'Valid email format'
                    : 'Invalid email format'}
                </p>
              </div>
            )}
          </div>

          {/* Password field with toggle visibility */}
          <div className="mt-4 relative">
            <Label className="text-white/80" htmlFor="password">
              Password
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={handlePasswordChange}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {/* Password strength indicator */}
            {password.length > 0 && password.length < 5 && (
              <div className="text-xs px-2 py-1.5 border rounded flex items-center gap-2 mt-1 bg-yellow-500/10 border-yellow-500/30 text-yellow-400">
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <p>Password should be at least 5 characters long</p>
              </div>
            )}
          </div>

          {/* Password requirements indicator */}
          <div className="text-xs space-y-1 px-2 py-1.5 bg-slate-900/70 rounded border border-slate-800">
            <p className="font-medium text-slate-300 mb-1">
              Password requirements:
            </p>
            <div className="flex items-center gap-1.5">
              <div
                className={`w-3 h-3 rounded-full ${password.length >= 5 ? 'bg-green-500' : 'bg-red-500'}`}
              ></div>
              <p
                className={`${password.length >= 5 ? 'text-green-400' : 'text-red-400'}`}
              >
                At least 5 characters{' '}
                {password.length > 0 && `(${password.length}/${5})`}
              </p>
            </div>
            <div className="flex items-center gap-1.5">
              <div
                className={`w-3 h-3 rounded-full ${/[!@#$%^&*(),.?":{}|<>]/.test(password) ? 'bg-green-500' : 'bg-red-500'}`}
              ></div>
              <p
                className={`${/[!@#$%^&*(),.?":{}|<>]/.test(password) ? 'text-green-400' : 'text-red-400'}`}
              >
                At least 1 special character{' '}
                {/[!@#$%^&*(),.?":{}|<>]/.test(password) ? '✓' : ''}
              </p>
            </div>
            {password.length > 0 && !validatePassword(password) && (
              <p className="text-amber-400 italic mt-1">
                Your password doesn&apos;t meet all requirements yet
              </p>
            )}
          </div>

          {/* Confirm Password field with toggle visibility */}
          <div className="relative">
            <Input
              placeholder="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              required
              className={`bg-black border-white/10 text-white placeholder:text-gray-400 pr-10 ${!passwordsMatch && confirmPassword ? 'border-red-500' : ''}`}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {/* Password match indicator */}
          {confirmPassword && (
            <div
              className={`text-xs px-2 py-1.5 border rounded flex items-center gap-2 ${passwordsMatch ? 'bg-green-500/10 border-green-500/30 text-green-400' : 'bg-red-500/10 border-red-500/30 text-red-400'}`}
            >
              <div
                className={`w-3 h-3 rounded-full ${passwordsMatch ? 'bg-green-500' : 'bg-red-500'}`}
              ></div>
              <p>
                {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
              </p>
            </div>
          )}

          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="updates"
                checked={emailOpt}
                onCheckedChange={(checked) => {
                  if (typeof checked === 'boolean') {
                    setEmailOpt(checked);
                  }
                }}
                className="border-white/10"
              />
              <label htmlFor="updates" className="text-sm text-gray-400">
                I would like to receive email updates about upcoming auctions
                and events
              </label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox id="terms" required className="border-white/10" />
              <label htmlFor="terms" className="text-sm text-gray-400">
                I agree to the{' '}
                <a
                  href="/terms"
                  className="text-blue-400 hover:text-blue-300 underline"
                >
                  Terms & Conditions
                </a>{' '}
                and{' '}
                <a
                  href="/privacy-policy"
                  className="text-blue-400 hover:text-blue-300 underline"
                >
                  Privacy Policy
                </a>
              </label>
            </div>
          </div>

          <div className="flex gap-4">
            <Button
              type="submit"
              className="flex-1 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 hover:text-blue-300 transition-all duration-300"
              disabled={!passwordValid || !passwordsMatch}
            >
              Create Account
            </Button>
            <Button
              type="button"
              onClick={onBackToLogin}
              className="flex-1 bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 hover:text-purple-300 transition-all duration-300"
            >
              Back to Login
            </Button>
          </div>

          {/* Add Google Sign In Button */}
          {onGoogleSignIn && (
            <>
              <div className="relative flex items-center py-2">
                <div className="flex-grow border-t border-gray-600"></div>
                <span className="flex-shrink mx-4 text-gray-400">or</span>
                <div className="flex-grow border-t border-gray-600"></div>
              </div>

              <Button
                type="button"
                onClick={onGoogleSignIn}
                className="w-full bg-transparent hover:bg-white/10 text-white flex items-center justify-center gap-2 transition-all duration-300 relative group overflow-hidden"
              >
                <FcGoogle className="text-xl" />
                <span>Sign up with Google</span>

                {/* Glow line animation */}
                <div className="absolute bottom-0 left-0 h-[2px] w-full bg-gradient-to-r from-blue-500/40 via-purple-500/40 to-blue-500/40 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-center" />

                {/* Subtle glow halo */}
                <div className="absolute inset-0 -z-10 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-r from-blue-500/5 via-purple-500/5 to-blue-500/5 blur-md" />
              </Button>

              {/* Add the disclaimer */}
              <p className="text-xs text-gray-500 text-center mt-2">
                By signing up with Google, you agree to receive email promotions
                by default. You can update your preferences in settings.
              </p>
            </>
          )}

          {displayError && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              {displayError}
            </div>
          )}
        </form>
      </div>
    </AuthLayout>
  );
}
